import { useEffect, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading, FormInput } from "go-form";
import { formatFormDateToString } from "go-core/components/Formatters/FormatDate";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { CancelSubscriptionApi } from "../../../../../../../services/Api/Subscription/subscriptionTypes";

interface Props {
	handleAction: (data?: CancelSubscriptionApi) => void;
	familyId: string;
	onHide: () => void;
	cancelDate?: string;
}

const CancelSubscriptionModal = (props: Props) => {
	const form = useForm<CancelSubscriptionApi>({
		criteriaMode: "all",
		defaultValues: {
			reason_details: "",
			reason: "OTHER",
			cancel_date: props.cancelDate,
		},
	});
	const { t } = useTranslation();
	const {
		register,
		handleSubmit,
		formState: { errors },
		control,
		watch,
	} = form;
	const [loading, setLoading] = useState(false);
	const cancelDate = watch("cancel_date");
	const [showCancelDateError, setShowCancelDateError] = useState<boolean>(false);

	const reasons = [
		{ label: t("enums.subscriptions.reasons_cancelling.EXPENSIVE"), value: "EXPENSIVE" },
		{ label: t("enums.subscriptions.reasons_cancelling.BUGS"), value: "BUGS" },
		{ label: t("enums.subscriptions.reasons_cancelling.SUPPORT"), value: "SUPPORT" },
		{ label: t("enums.subscriptions.reasons_cancelling.LACK_OF_CONTACT"), value: "LACK_OF_CONTACT" },
		{ label: t("enums.subscriptions.reasons_cancelling.ONLY_TESTING"), value: "ONLY_TESTING" },
		{ label: t("enums.subscriptions.reasons_cancelling.OTHER"), value: "OTHER" },
		{ label: t("enums.subscriptions.reasons_cancelling.CLOSED"), value: "CLOSED" },
	];

	useEffect(() => {
		if (showCancelDateError) {
			setShowCancelDateError(false);
		}
	}, [cancelDate]);

	const onSubmit = handleSubmit((data: CancelSubscriptionApi) => {
		setLoading(true);
		if (validateCancelDate()) {
			const date = formatFormDateToString(data.cancel_date);
			data.cancel_date = `${date
				.toLocaleDateString("en-GB", {
					year: "numeric",
					month: "2-digit",
					day: "2-digit",
				})
				.split("/")
				.reverse()
				.join("-")}T23:59:59`;
			props.handleAction(data);
		}
		setLoading(false);
	});

	const getCancelDateInputDefaultValue = () => {
		return props.cancelDate ? props.cancelDate : "";
	};

	const validateCancelDate = () => {
		let isValid = true;
		setShowCancelDateError(false);
		const cancellingDate = cancelDate ?? getCancelDateInputDefaultValue();
		if (props.cancelDate) {
			if (formatFormDateToString(cancellingDate) < formatFormDateToString(props.cancelDate)) {
				isValid = false;
				setShowCancelDateError(true);
			}
		}
		return isValid;
	};

	const getCancelDateErrorText = () => {
		if (props.cancelDate) return t("modules.subscription.field.wrong_cancellation_date.title");
		return t("modules.subscription.field.today_subscription_date.title");
	};

	return (
		<Modal show onHide={props.onHide}>
			<Form noValidate onSubmit={onSubmit}>
				<Modal.Header closeButton>
					<Modal.Title>
						{t("modules.subscription.action.cancel_subscription.title")} "{props.familyId}"
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Alert variant="danger">{t("modules.subscription.field.cancellation_warning.title")}</Alert>
					<FormInput errors={errors} type={"hidden"} register={register} name={"family_id"} />
					<FormDatePicker
						label={t("modules.subscription.field.cancel_date.title")}
						control={control}
						name="cancel_date"
						allowClear={false}
						timePicker={false}
						errors={errors}
						defaultValue={getCancelDateInputDefaultValue()}
					/>
					{showCancelDateError && <div className="invalid-feedback d-block">{getCancelDateErrorText()}</div>}
					<FormSelectGroup
						label={t("modules.subscription.field.reason.title")}
						name="reason"
						errors={errors}
						options={reasons}
						control={control}
						data-testid="reason"
					/>
					<FormInput
						as={"textarea"}
						rows={3}
						label={t("modules.subscription.field.reason_with_details.title")}
						register={register}
						name="reason_details"
						errors={errors}
					/>
				</Modal.Body>
			</Form>
			<Modal.Footer>
				<ButtonLoading loading={loading} type="submit" onClick={onSubmit}>
					{t("modules.subscription.action.cancel_subscription.title")}
				</ButtonLoading>
				<Button variant="light" onClick={props.onHide}>
					{t("common.action.cancel", { ns: "lib" })}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default CancelSubscriptionModal;
